import {
  Plugins,
  EnvTypes,
  TypeImplementation,
  Result,
  Expressions,
} from '@packages/systems/wfdl';
import {
  makeCollectionFunctionType,
  makeCollectionAliasTypes,
} from '@packages/systems/dynamo/wfdl-mappings';
import {
  functionOf,
  TText,
  TList,
  TRecord,
  TUnion,
  TEmptyRecord,
  ECall,
  EHole,
} from '@packages/systems/wfdl-ast';
import {iconName, pluginName, qualify} from '@packages/systems/wfdl-Qualified';

import {forall, tyfun, a} from '@packages/utilities/wfdl-helpers';
import {toBooleanTag, TBoolean} from '@packages/systems/plugins-utils/Boolean';
import {type CollectionsType} from '@packages/systems/core/records/CollectionStateType';
import type {ComponentForms} from '@packages/systems/flows/types';
import {LOGIC_BLOCK_CATEGORIES} from '@packages/systems/flows/shared';
import {weakMemo} from '@packages/utilities/memo';

import {
  makeFormPluginValues,
  formTriggerValue,
  FormParserType,
  formFieldsToUiType,
  formFieldToType,
  getFormPluginNameFromExpr,
  generateFormName,
  generateTitleFormName,
  generateFormParser,
  formSubmissionIcons,
} from './forms';
import {webhookValue, webhookIcons, WebhookParserType} from './webhook';

export {
  formFieldsToUiType,
  formFieldToType,
  makeFormPluginValues,
  formTriggerValue,
  getFormPluginNameFromExpr,
  generateFormName,
  generateTitleFormName,
  generateFormParser,
};

export const make__SitePluginValuesForLF = weakMemo(
  (collections: CollectionsType) =>
    weakMemo((componentForms: ComponentForms) => {
      const collectionFunctions: {
        [key: string]: EnvTypes.TypedValue;
      } = collections.reduce<Record<string, any>>(
        makeCollectionFunctionType,
        {}
      );

      return {
        ...makeFormPluginValues(componentForms),
        ...(collectionFunctions as any),
      };
    })
);

export const make__SitePluginAliasTypesForLF = (
  collections: CollectionsType
): {
  [key: string]: TypeImplementation.TypeImplementation;
} => {
  return makeCollectionAliasTypes(collections);
};

export const plugin = Plugins.PrivilegedPlugin({
  icons: {
    ...formSubmissionIcons,
    ...webhookIcons,
  },
  dataTypes: {
    WebhookParser: WebhookParserType,
    FormParser: FormParserType,
    FormResult: TypeImplementation.Custom({
      parseValue: Result.Ok,
      render: () => null,
    }),
  },
  valuePresets: {
    formTriggerFactory: {
      factory: ECall({
        fun: Expressions.qualifiedVariable('Logic', 'formTrigger'),
        arg: EHole(),
      }),
      label: 'Form submission',
      icon: {
        medium: qualify(pluginName('Logic'), iconName('FormSubmissionDetail')),
        small: qualify(pluginName('Logic'), iconName('ElementFormBlockIcon')),
      },
      helpData: {
        description:
          'Runs when a form in your Webflow project has been submitted',
      },
      category: LOGIC_BLOCK_CATEGORIES.Triggers,
    },

    webhookFactory: {
      factory: ECall({
        fun: Expressions.qualifiedVariable('Logic', 'webhook'),
        arg: EHole(),
      }),
      label: 'Incoming webhook',
      icon: {
        medium: qualify(pluginName('Logic'), iconName('WebhooksDetail')),
        small: qualify(pluginName('Logic'), iconName('Webhooks')),
      },
      helpData: {
        description: 'Runs when an event in an external service occurs',
      },
      category: LOGIC_BLOCK_CATEGORIES.Triggers,
    },
  },

  values: {
    ...formTriggerValue,
    ...webhookValue,

    // TODO: Figure out where to put this plugin value, perhaps a plugin
    // that deals with association lists (e.g. list of name/value pairs)
    'Maybe get field': {
      envType: {
        dataType: forall(
          ['a'],
          tyfun(
            [TText, TList(TRecord({name: TText, value: a}))],
            TUnion({
              None: TEmptyRecord,
              Some: a,
            })
          )
        ),
        exported: 'hidden',
      },
      value: function maybeGetField_fieldName(fieldName: string) {
        return function maybeGetField_listOfFields(
          listOfFields: Array<{
            name: string;
            value: string;
          }>
        ):
          | {
              tag: 'Some';
              val: string;
            }
          | {
              tag: 'None';
              val: Record<any, any>;
            } {
          for (const {name, value} of listOfFields) {
            if (name === fieldName) return {tag: 'Some', val: value};
          }

          return {
            tag: 'None',
            val: {},
          };
        };
      },
    },

    // TODO: Create a List plugin and put this in there:
    'List Includes': {
      envType: {
        exported: 'hidden',
        dataType: functionOf(TList(TText), functionOf(TText, TBoolean)),
      },

      // @ts-expect-error - TS7006 - Parameter 'list' implicitly has an 'any' type.
      value: (list) => (text: any) => {
        const index = list.indexOf(text);
        return toBooleanTag(index !== -1);
      },
    },
  },
});
